import { inject, Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { OperatorService, WorkingTurnService } from '@vasta/api-vasta';

/** The two steps of the wizard that are not optional. */
export type PendingOnboardingStep = '4' | '5';

@Injectable({ providedIn: 'root' })
export class TenantLandingService {
  private readonly operators = inject(OperatorService);
  private readonly workingTurns = inject(WorkingTurnService);

  /**
   * The tenants this session has already made up its mind about.
   *
   * The gate is a decision per *landing*, not per navigation: once a user is
   * inside a workspace they move around it freely, including the wizard's own
   * "finish later". A reload is a new session and therefore a new landing, which
   * is what makes D6 hold across sessions — a tenant without operators is put
   * back in the wizard every time it is opened, and never mid-visit.
   */
  private readonly settled = new Set<string>();

  /**
   * Which onboarding step this landing owes, or `null` to leave it where it is.
   *
   * `tenantId` has to be the tenant the URL already carries, and `url` the URL
   * it is at: the `x-tenant-id` header is read off the active route, so a check
   * made *during* the navigation that first enters a tenant would ask the API
   * about nobody. That is why this runs after the landing rather than in a
   * guard, and why the answer is a step to move to rather than a decision to
   * refuse the route.
   *
   * The derived state is only ever "no operators" or "no turns" — steps 2 and 3
   * leave nothing behind to read, so they cannot be resumed into (D6, §1).
   */
  divert(tenantId: string, url: string): Observable<PendingOnboardingStep | null> {
    if (tenantId.length === 0 || this.isWizard(url, tenantId)) {
      return of(null);
    }

    if (this.settled.has(tenantId)) {
      return of(null);
    }

    // Settled only once there is an answer: a check that could not be made is
    // not a decision, and the next landing tries again.
    return this.pendingStep().pipe(
      tap(() => this.settled.add(tenantId)),
      catchError(() => of(null)),
    );
  }

  /**
   * The step a tenant still owes, asked of the two contexts that own the data.
   *
   * Both reads are `limit: 1`: the question is whether there is any, and a page
   * of rows to count to one would be a page of rows nobody looks at.
   *
   * A failure is left to the caller: the check is what sends somebody into the
   * wizard, and a check that cannot be made must not be the reason the app
   * refuses to open — but nor is it an answer worth remembering.
   */
  private pendingStep(): Observable<PendingOnboardingStep | null> {
    return forkJoin({
      operators: this.operators.findAllOperator({ limit: 1, offset: 0 }),
      turns: this.workingTurns.findAllWorkingTurn({ limit: 1, offset: 0 }),
    }).pipe(
      map(({ operators, turns }) => {
        if (operators.totalItems === 0) return '4' as const;
        if (turns.totalItems === 0) return '5' as const;
        return null;
      }),
    );
  }

  /**
   * Whether the user is already inside the wizard.
   *
   * A reload of step 3 must not throw the user forward to step 4: the wizard is
   * walked in order, and which step is on screen is the user's position in it,
   * not a fact to recompute. Steps 2 and 3 leave nothing behind, so there is
   * nothing to recompute it from anyway.
   */
  private isWizard(url: string, tenantId: string): boolean {
    return url.startsWith(`/${tenantId}/onboarding`);
  }
}
